import type { LearnerState, ChatRequest, StateUpdateRequest, AssessmentResponse } from './types';

const rawBase =
  (import.meta as { env?: { VITE_API_BASE?: string } }).env?.VITE_API_BASE ?? '';
const API_BASE =
  rawBase === '' ? '/api' : rawBase.replace(/\/api\/?$/, '') + '/api';

export async function createLearnerState(
  moduleId: string,
  educatorName?: string
): Promise<LearnerState> {
  const response = await fetch(`${API_BASE}/state`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ moduleId, educatorName }),
  });

  if (!response.ok) {
    throw new Error(`Failed to create learner state: ${response.statusText}`);
  }

  return response.json();
}

export async function getLearnerState(learnerId: string): Promise<LearnerState> {
  const response = await fetch(`${API_BASE}/state/${learnerId}`);

  if (!response.ok) {
    throw new Error(`Failed to get learner state: ${response.statusText}`);
  }

  return response.json();
}

export async function updateLearnerState(request: StateUpdateRequest): Promise<LearnerState> {
  const response = await fetch(`${API_BASE}/state/${request.learnerId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    throw new Error(`Failed to update learner state: ${response.statusText}`);
  }

  return response.json();
}

export async function sendChatMessage(request: ChatRequest): Promise<ReadableStream> {
  const response = await fetch(`${API_BASE}/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    throw new Error(`Failed to send chat message: ${response.statusText}`);
  }

  if (!response.body) {
    throw new Error('Response body is null');
  }

  return response.body;
}

export async function submitAssessment(
  learnerId: string,
  moduleId: string,
  partId: string,
  responses: AssessmentResponse[]
): Promise<{
  responses: AssessmentResponse[];
  overallScore: number;
  correctCount: number;
  totalQuestions: number;
}> {
  const response = await fetch(`${API_BASE}/assessment/submit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      learnerId,
      moduleId,
      partId,
      responses,
    }),
  });

  if (!response.ok) {
    throw new Error(`Failed to submit assessment: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Save the voice reflection transcript to the backend so it's included in the JSON export.
 */
export async function saveVoiceTranscript(
  learnerId: string,
  moduleId: string,
  messages: Array<{ role: 'user' | 'assistant'; content: string }>
): Promise<void> {
  const response = await fetch(`${API_BASE}/voice/transcript`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ learnerId, moduleId, messages }),
  });

  if (!response.ok) {
    throw new Error(`Failed to save voice transcript: ${response.statusText}`);
  }
}

/**
 * Load conversation history from the backend.
 * Used to restore chat UI when resuming a session after refresh.
 */
export async function getConversationHistory(
  learnerId: string,
  moduleId: string,
  partId: string,
  phase: string
): Promise<import('./types').Message[]> {
  const params = new URLSearchParams({ learnerId, moduleId, partId, phase });
  const response = await fetch(`${API_BASE}/chat/history?${params}`);
  if (!response.ok) return [];
  return response.json();
}

/**
 * Export full learner data as a structured JSON download.
 * Fetches all state + conversations from the backend and triggers a file download.
 */
export async function exportLearnerData(learnerId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/export/${learnerId}`);

  if (!response.ok) {
    throw new Error(`Failed to export learner data: ${response.statusText}`);
  }

  const data = await response.json() as { learner?: { educatorName?: string } };

  // Use educator name in filename if available
  const name = data?.learner?.educatorName
    ? data.learner.educatorName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
    : learnerId.slice(0, 8);
  const date = new Date().toISOString().slice(0, 10);

  // Create a downloadable JSON file
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `itari-report-${name}-${date}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
